const ResumeDisplay = ({ data }) => {
  if (!data) return null;

  // Helper: Render a string as a list if it contains list-defining characters
  function renderStringOrList(str) {
    const listChars = ['•', '-', '*', '‣', '▪', '–'];
    // If the string contains any list character, split and render as a list
    if (listChars.some(char => str.includes(char))) {
      // Split on newlines, filter out empty, trim, and remove leading list char
      const items = str
        .split(/\n/)
        .map(line => line.trim())
        .filter(line => listChars.some(char => line.startsWith(char)))
        .map(line => line.replace(/^([•\-*‣▪–])\s*/, ''));
      if (items.length > 0) {
        return (
          <ul className="list-disc pl-6">
            {items.map((item, idx) => (
              <li key={idx} className="text-sm text-gray-800 dark:text-gray-200">{item}</li>
            ))}
          </ul>
        );
      }
    }
    // Otherwise, render as a normal paragraph
    return (
      <p className="whitespace-pre-line text-sm text-gray-800 dark:text-gray-200">
        {str}
      </p>
    );
  }

  // Render a section, omitting the header for object keys (just show the value)
  const renderSection = (title, items) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 mb-4">
      <h2 className="text-xl font-semibold mb-2">{title}</h2>
      {items.map((item, idx) => {
        if (typeof item === 'object' && item !== null) {
          return Object.entries(item).map(([key, value], i) => (
            <div key={i} className="mb-2">
              {typeof value === "string" ? renderStringOrList(value) : value}
            </div>
          ));
        }
        return (
          <div key={idx}>
            {typeof item === "string" ? renderStringOrList(item) : null}
          </div>
        );
      })}
    </div>
  );

  // Special rendering for work experience/projects for clarity
  const renderWorkExperience = (items) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 mb-4">
      <h2 className="text-xl font-semibold mb-2">Projects & Experience</h2>
      {items.map((item, idx) => {
        // Standard work experience object
        if (
          typeof item === 'object' &&
          (item.jobtitle || item.organization || item.date_start || item.date_end || item.text)
        ) {
          return (
            <div key={idx} className="mb-4 p-3 border-b border-gray-200 dark:border-gray-700">
              {item.jobtitle && (
                <div className="font-semibold">{item.jobtitle}</div>
              )}
              {item.organization && (
                <div className="italic">{item.organization}</div>
              )}
              {(item.date_start || item.date_end) && (
                <div className="text-xs text-gray-500 mb-1">
                  {item.date_start} {item.date_start && item.date_end && '–'} {item.date_end}
                </div>
              )}
              {item.text && (
                <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line">
                  {renderStringOrList(item.text)}
                </div>
              )}
            </div>
          );
        }
        // Generic section object (e.g., { "Projects": "..." })
        if (typeof item === 'object' && item !== null) {
          return Object.entries(item).map(([key, value], i) => (
            <div key={i} className="mb-2">
              {typeof value === "string" ? renderStringOrList(value) : value}
            </div>
          ));
        }
        // Fallback for string or other types
        return (
          <div key={idx}>
            {typeof item === "string" ? renderStringOrList(item) : null}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <h2>Resume Processed Sucessfully</h2>
      {/* Resume Sections */}
      {data.summary && renderSection("Objective", data.summary)}
      {data.education_and_training && renderSection("Education & Training", data.education_and_training)}
      {data.skills && renderSection("Skills", data.skills)}
      {data.accomplishments && renderSection("Accomplishments", data.accomplishments)}
      {data.awards && renderSection("Certifications & Awards", data.awards)}
      {data.credibility && renderSection("References", data.credibility)}
      {data.extracurricular && renderSection("Extracurricular Activities", data.extracurricular)}
      {data.work_experience && renderWorkExperience(data.work_experience)}
      {data.misc && renderSection("Miscellaneous", data.misc)}
    </div>
  );
};

export default ResumeDisplay;